import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Card,
  CardContent,
  IconButton,
  Grid,
  Alert,
  Paper,
  Divider,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import {
  WorkflowStepData,
  StepType,
  SubmitStepPayload,
  KnowledgeItem,
  KnowledgeFieldSchema,
  FileDetails,
} from "../../../types/process";
import FileInput from "../../../components/process/UI/FileInput";

interface StepConfigurationFormProps {
  step: WorkflowStepData;
  categoryId: string;
  processId: string;
  onSave: (payload: SubmitStepPayload) => void;
  onCancel: () => void;
}

const StepConfigurationForm: React.FC<StepConfigurationFormProps> = ({
  step,
  categoryId,
  processId,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [hybridSelection, setHybridSelection] = useState<
    "app" | "knowledge" | null
  >(
    step.type === StepType.HYBRID
      ? step.currentConfig?.selectedAppId
        ? "app"
        : step.currentConfig?.knowledgeData !== undefined
          ? "knowledge"
          : null
      : null,
  );

  const initialAppId =
    step.currentConfig?.selectedAppId ||
    (step.config?.availableApps && step.config.availableApps.length > 0
      ? step.config.availableApps[0].id
      : "");
  const [selectedAppId, setSelectedAppId] = useState<string>(initialAppId);

  const [knowledgeItems, setKnowledgeItems] = useState<KnowledgeItem[]>(
    step.config?.isStructuredKnowledge &&
      Array.isArray(step.currentConfig?.knowledgeData)
      ? JSON.parse(JSON.stringify(step.currentConfig.knowledgeData))
      : step.config?.isStructuredKnowledge
        ? [{ id: uuidv4() }]
        : [],
  );

  const [newlySelectedFiles, setNewlySelectedFiles] = useState<{
    [key: string]: File[];
  }>({});

  const [unstructuredKnowledge, setUnstructuredKnowledge] = useState<string>(
    !step.config?.isStructuredKnowledge &&
      typeof step.currentConfig?.knowledgeData === "string"
      ? step.currentConfig.knowledgeData
      : "",
  );

  const [appInstallationParams, setAppInstallationParams] = useState<{
    [key: string]: any;
  }>(() => {
    // Pre-populate with existing parameters if app is already configured
    if (
      step.currentConfig?.selectedAppId &&
      step.currentConfig?.appInstallationParams
    ) {
      return { ...step.currentConfig.appInstallationParams };
    }
    return {};
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const selectedAppDetails = step.config?.availableApps?.find(
    app => app.id === selectedAppId,
  );
  const needsAppInstallation =
    selectedAppDetails?.requiredInputs &&
    (!selectedAppDetails?.isInstalled ||
      step.currentConfig?.appInstallationParams);

  useEffect(() => {
    if (step.type === StepType.HYBRID) {
      if (hybridSelection === "app") {
        setSelectedAppId(
          current => current || step.config?.availableApps?.[0]?.id || "",
        );
        setKnowledgeItems([]);
        setUnstructuredKnowledge("");
      } else if (hybridSelection === "knowledge") {
        setSelectedAppId("");
        if (step.config?.isStructuredKnowledge) {
          setKnowledgeItems(
            Array.isArray(step.currentConfig?.knowledgeData) &&
              step.currentConfig?.knowledgeData.length > 0
              ? JSON.parse(JSON.stringify(step.currentConfig.knowledgeData))
              : [{ id: uuidv4() }],
          );
          setUnstructuredKnowledge("");
        } else {
          setKnowledgeItems([]);
          setUnstructuredKnowledge(
            typeof step.currentConfig?.knowledgeData === "string"
              ? step.currentConfig.knowledgeData
              : "",
          );
        }
      }
    } else if (step.type === StepType.APP) {
      setSelectedAppId(
        current => current || step.config?.availableApps?.[0]?.id || "",
      );
    } else if (step.type === StepType.KNOWLEDGE) {
      setSelectedAppId("");
      if (step.config?.isStructuredKnowledge) {
        setKnowledgeItems(
          Array.isArray(step.currentConfig?.knowledgeData) &&
            step.currentConfig?.knowledgeData.length > 0
            ? JSON.parse(JSON.stringify(step.currentConfig.knowledgeData))
            : [{ id: uuidv4() }],
        );
      } else {
        setUnstructuredKnowledge(
          typeof step.currentConfig?.knowledgeData === "string"
            ? step.currentConfig.knowledgeData
            : "",
        );
      }
    }
    // Only reset appInstallationParams if it's a new configuration
    if (!step.currentConfig?.appInstallationParams) {
      setAppInstallationParams({});
    }
    setNewlySelectedFiles({});
  }, [step, hybridSelection]);

  useEffect(() => {
    // When app changes, preserve existing params if editing the same app, otherwise reset
    if (
      step.currentConfig?.selectedAppId === selectedAppId &&
      step.currentConfig?.appInstallationParams
    ) {
      setAppInstallationParams({ ...step.currentConfig.appInstallationParams });
    } else {
      setAppInstallationParams({});
    }
  }, [selectedAppId, step.currentConfig]);

  const handleKnowledgeItemChange = (
    itemId: string,
    fieldName: string,
    value: any,
  ) => {
    setKnowledgeItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, [fieldName]: value } : item,
      ),
    );
  };

  const handleNewFileChange = (
    itemId: string,
    fieldName: string,
    files: File[],
  ) => {
    setNewlySelectedFiles(prev => ({
      ...prev,
      [`${itemId}-${fieldName}`]: files,
    }));
  };

  const handleRemoveExistingFile = (
    itemId: string,
    fieldName: string,
    fileIdToRemove: string,
  ) => {
    setKnowledgeItems(prevItems =>
      prevItems.map(item => {
        if (item.id === itemId) {
          const existingFiles = (item[fieldName] as FileDetails[]) || [];
          return {
            ...item,
            [fieldName]: existingFiles.filter(f => f.id !== fileIdToRemove),
          };
        }
        return item;
      }),
    );
  };

  const addKnowledgeItem = () => {
    if (
      step.config?.knowledgeSchema &&
      (!step.config.maxKnowledgeItems ||
        knowledgeItems.length < step.config.maxKnowledgeItems)
    ) {
      setKnowledgeItems(prev => [...prev, { id: uuidv4() }]);
    }
  };

  const removeKnowledgeItem = (itemId: string) => {
    setKnowledgeItems(prev => prev.filter(item => item.id !== itemId));
    setNewlySelectedFiles(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        if (key.startsWith(`${itemId}-`)) {
          delete updated[key];
        }
      });
      return updated;
    });
  };

  const handleAppParamChange = (paramName: string, value: string) => {
    setAppInstallationParams(prev => ({ ...prev, [paramName]: value }));
  };

  const isAppInstallationFormValid = () => {
    if (!needsAppInstallation || !selectedAppDetails?.requiredInputs)
      return true;
    return selectedAppDetails.requiredInputs.every(input => {
      if (!input.required) return true;
      return (
        appInstallationParams[input.name] &&
        String(appInstallationParams[input.name]).trim() !== ""
      );
    });
  };

  const isKnowledgeFormValid = (): boolean => {
    if (!step.config?.isStructuredKnowledge || !step.config.knowledgeSchema)
      return true;

    for (const item of knowledgeItems) {
      for (const schema of step.config.knowledgeSchema) {
        if (schema.required) {
          const value = item[schema.name];
          const newFilesKey = `${item.id}-${schema.name}`;
          const pendingNewFiles = newlySelectedFiles[newFilesKey] || [];

          if (schema.type === "file") {
            if (
              (!value || (value as FileDetails[]).length === 0) &&
              pendingNewFiles.length === 0
            ) {
              return false;
            }
          } else {
            if (
              value === undefined ||
              value === null ||
              String(value).trim() === ""
            ) {
              if (typeof value === "boolean") continue;
              return false;
            }
          }
        }
      }
    }
    return true;
  };

  const isSaveDisabled = () => {
    if (step.type === StepType.HYBRID && hybridSelection === null) return true;
    if (
      step.type === StepType.APP ||
      (step.type === StepType.HYBRID && hybridSelection === "app")
    ) {
      if (!selectedAppId) return true;
      if (needsAppInstallation && !isAppInstallationFormValid()) return true;
    }
    if (
      step.type === StepType.KNOWLEDGE ||
      (step.type === StepType.HYBRID && hybridSelection === "knowledge")
    ) {
      if (!isKnowledgeFormValid()) return true;
    }
    return false;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaveDisabled()) return;

    let configToSubmit: any = {};

    if (
      step.type === StepType.APP ||
      (step.type === StepType.HYBRID && hybridSelection === "app")
    ) {
      if (!selectedAppId) {
        setErrors({ general: t("please_select_application") });
        return;
      }
      configToSubmit = { selectedAppId };
      if (step.type === StepType.HYBRID) configToSubmit.type = StepType.APP;

      if (needsAppInstallation && isAppInstallationFormValid()) {
        configToSubmit.appInstallationParams = appInstallationParams;
      } else if (needsAppInstallation && !isAppInstallationFormValid()) {
        setErrors({ general: t("fill_required_app_fields") });
        return;
      }
    } else if (
      step.type === StepType.KNOWLEDGE ||
      (step.type === StepType.HYBRID && hybridSelection === "knowledge")
    ) {
      if (step.config?.isStructuredKnowledge) {
        const processedKnowledgeItems = knowledgeItems.map(item => {
          const processedItem: KnowledgeItem = { ...item };
          step.config?.knowledgeSchema?.forEach(schema => {
            if (schema.type === "file") {
              const newFilesKey = `${item.id}-${schema.name}`;
              const pendingNewFiles = newlySelectedFiles[newFilesKey] || [];
              const existingFileDetails =
                (item[schema.name] as FileDetails[]) || [];

              // Combine existing FileDetails with new File objects
              // The StepDetailModal will handle File objects properly
              processedItem[schema.name] = [
                ...existingFileDetails,
                ...pendingNewFiles,
              ];
            }
          });
          return processedItem;
        });
        configToSubmit = {
          knowledgeData: processedKnowledgeItems,
          internalAppName: step.name, // Add the step name as internalAppName for structured knowledge
        };
      } else {
        configToSubmit = { knowledgeData: unstructuredKnowledge };
      }
      if (step.type === StepType.HYBRID)
        configToSubmit.type = StepType.KNOWLEDGE;
    }

    onSave({
      categoryId,
      processId,
      stepId: step.id,
      config: configToSubmit,
    });
  };

  const renderKnowledgeField = (
    fieldSchema: KnowledgeFieldSchema,
    item: KnowledgeItem,
    itemIndex: number,
  ) => {
    const key = `knowledge-item-${item.id}-${fieldSchema.name}`;
    const value = item[fieldSchema.name];

    switch (fieldSchema.type) {
      case "number":
        return (
          <TextField
            key={key}
            label={`${fieldSchema.label}${fieldSchema.required ? " *" : ""}`}
            type="number"
            name={`${item.id}-${fieldSchema.name}`}
            value={value || ""}
            placeholder={fieldSchema.placeholder}
            onChange={e =>
              handleKnowledgeItemChange(
                item.id,
                fieldSchema.name,
                parseFloat(e.target.value) || 0,
              )
            }
            fullWidth
            margin="normal"
            size="small"
          />
        );
      case "textarea":
        return (
          <TextField
            key={key}
            label={`${fieldSchema.label}${fieldSchema.required ? " *" : ""}`}
            name={`${item.id}-${fieldSchema.name}`}
            value={value || ""}
            placeholder={fieldSchema.placeholder}
            onChange={e =>
              handleKnowledgeItemChange(
                item.id,
                fieldSchema.name,
                e.target.value,
              )
            }
            fullWidth
            multiline
            rows={4}
            margin="normal"
            size="small"
          />
        );
      case "boolean":
        return (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                checked={!!value}
                onChange={e =>
                  handleKnowledgeItemChange(
                    item.id,
                    fieldSchema.name,
                    e.target.checked,
                  )
                }
              />
            }
            label={`${fieldSchema.label}${fieldSchema.required ? " *" : ""}`}
          />
        );
      case "file":
        return (
          <FileInput
            key={key}
            id={`${item.id}-${fieldSchema.name}`}
            label={`${fieldSchema.label}${fieldSchema.required ? " *" : ""}`}
            fieldName={fieldSchema.name}
            config={fieldSchema.fileConfig}
            currentFiles={Array.isArray(value) ? value : []}
            onChange={files =>
              handleNewFileChange(item.id, fieldSchema.name, files)
            }
            onRemoveExisting={fileId =>
              handleRemoveExistingFile(item.id, fieldSchema.name, fileId)
            }
            required={fieldSchema.required}
          />
        );
      case "text":
      default:
        return (
          <TextField
            key={key}
            label={`${fieldSchema.label}${fieldSchema.required ? " *" : ""}`}
            type="text"
            name={`${item.id}-${fieldSchema.name}`}
            value={value || ""}
            placeholder={fieldSchema.placeholder}
            onChange={e =>
              handleKnowledgeItemChange(
                item.id,
                fieldSchema.name,
                e.target.value,
              )
            }
            fullWidth
            margin="normal"
            size="small"
          />
        );
    }
  };

  const renderAppConfiguration = () => (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "medium" }}>
        {t("select_application")}:
      </Typography>
      <Grid container spacing={2}>
        {step.config?.availableApps?.map(app => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={app.id}>
            <Card
              sx={{
                cursor: "pointer",
                border: app.id === selectedAppId ? 2 : 1,
                borderColor:
                  app.id === selectedAppId ? "primary.main" : "divider",
                transition: "all 0.2s",
                "&:hover": {
                  boxShadow: 2,
                },
              }}
              onClick={() => setSelectedAppId(app.id)}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
                    {app.name}
                  </Typography>
                  {app.isInstalled && (
                    <CheckCircleOutlineIcon
                      sx={{ color: theme.palette.success.main, fontSize: 20 }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {app.isInstalled
                    ? t("ready_to_use")
                    : app.requiredInputs
                      ? t("requires_setup")
                      : t("ready_to_install")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {selectedAppId &&
        needsAppInstallation &&
        selectedAppDetails &&
        selectedAppDetails.requiredInputs && (
          <Paper sx={{ mt: 3, p: 3, backgroundColor: "#eeeeee" }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {step.currentConfig?.appInstallationParams
                ? t("reconfigure")
                : t("setup_for")}{" "}
              {selectedAppDetails.name}
            </Typography>
            {selectedAppDetails.requiredInputs.map(inputSchema => (
              <Box key={inputSchema.name} sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                    {inputSchema.label}{" "}
                    {inputSchema.required && (
                      <span style={{ color: theme.palette.error.main }}>*</span>
                    )}
                  </Typography>
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Box>
                <TextField
                  type={inputSchema.type}
                  id={inputSchema.name}
                  name={inputSchema.name}
                  value={appInstallationParams[inputSchema.name] || ""}
                  onChange={e =>
                    handleAppParamChange(inputSchema.name, e.target.value)
                  }
                  placeholder={inputSchema.placeholder}
                  required={inputSchema.required}
                  fullWidth
                  size="small"
                />
              </Box>
            ))}
            {!isAppInstallationFormValid() && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {t("fill_required_app_fields")}
              </Alert>
            )}
          </Paper>
        )}
    </Box>
  );

  const renderKnowledgeConfiguration = () => {
    if (step.config?.isStructuredKnowledge) {
      return (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("structured_knowledge")}
          </Typography>
          {!isKnowledgeFormValid() && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t("fill_required_fields")}
            </Alert>
          )}
          {knowledgeItems.map((item, index) => (
            <Paper key={item.id} sx={{ p: 2, mb: 2, position: "relative" }}>
              {step.config?.knowledgeSchema?.map(fieldSchema =>
                renderKnowledgeField(fieldSchema, item, index),
              )}
              {knowledgeItems.length > 1 && (
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => removeKnowledgeItem(item.id)}
                  sx={{ position: "absolute", top: 8, right: 8 }}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Paper>
          ))}
          {(!step.config.maxKnowledgeItems ||
            knowledgeItems.length < step.config.maxKnowledgeItems) && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addKnowledgeItem}
            >
              {t("add")}{" "}
              {step.config.knowledgeSchema?.[0]?.label.split(" ")[0] ||
                t("item")}
            </Button>
          )}
        </Box>
      );
    }
    return (
      <TextField
        label={t("knowledge_details")}
        value={unstructuredKnowledge}
        onChange={e => setUnstructuredKnowledge(e.target.value)}
        placeholder={t("enter_knowledge_information")}
        multiline
        rows={6}
        fullWidth
      />
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        {t("configure_step")}: {step.name}
      </Typography>

      {step.type === StepType.HYBRID && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {t("configuration_type")}:
          </Typography>
          <RadioGroup
            row
            value={hybridSelection}
            onChange={e =>
              setHybridSelection(e.target.value as "app" | "knowledge")
            }
          >
            <FormControlLabel
              value="app"
              control={<Radio />}
              label={t("use_app")}
            />
            <FormControlLabel
              value="knowledge"
              control={<Radio />}
              label={t("use_knowledge")}
            />
          </RadioGroup>
          {hybridSelection === null && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {t("select_configuration_type")}
            </Alert>
          )}
        </Box>
      )}

      {step.type === StepType.APP && renderAppConfiguration()}
      {step.type === StepType.KNOWLEDGE && renderKnowledgeConfiguration()}

      {step.type === StepType.HYBRID &&
        hybridSelection === "app" &&
        renderAppConfiguration()}
      {step.type === StepType.HYBRID &&
        hybridSelection === "knowledge" &&
        renderKnowledgeConfiguration()}

      {errors.general && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errors.general}
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button variant="outlined" onClick={onCancel}>
          {t("cancel")}
        </Button>
        <Button variant="contained" type="submit" disabled={isSaveDisabled()}>
          {needsAppInstallation &&
          selectedAppId &&
          (step.type === StepType.APP ||
            (step.type === StepType.HYBRID && hybridSelection === "app"))
            ? t("install_app_and_save")
            : t("save_configuration")}
        </Button>
      </Box>
    </form>
  );
};

export default StepConfigurationForm;
